// BasicSelectV3.tsx
import React, { useCallback, useEffect, useMemo, useState, ReactNode, ReactElement } from 'react';
import { StyledBasicSelect } from './style.ts';
import { themeOptions } from 'styles';
import { commonImage, arrowImage } from 'constants/images';

import { MenuItem, Select, SelectProps } from '@mui/material';
import { MenuItemProps } from '@mui/material/MenuItem';
import { SelectChangeEvent } from '@mui/material/Select';

interface BasicSelectV3Props extends Omit<SelectProps, 'onChange' | 'value'> {
  name?: string;
  width?: string;
  label?: string;
  labelName?: string;
  valueName?: string;
  options: Array<Record<string, any>>;
  required?: boolean;
  sendValue?: (event: SelectChangeEvent, child: ReactElement<MenuItemProps>) => void;
  value?: string | number;
  className?: string;
  onSelected?: (props: MenuItemProps) => void;
  placeholder?: string;
  marginBottom?: string;
  disabled?: boolean;
  isUsePadding?: boolean;
}

export const BasicSelectV3: React.FC<BasicSelectV3Props> = ({
  name,
  width,
  label,
  labelName,
  valueName,
  options,
  required,
  sendValue,
  value,
  className,
  onSelected,
  placeholder = 'Select',
  marginBottom,
  disabled,
  isUsePadding = true,
  ...props
}) => {
  const [selectedOption, setSelectedOption] = useState<string | number | undefined>(value);
  const [selectOpen, setSelectOpen] = useState<boolean>(false);

  const selectOptions = useMemo(() => {
    const result = options.map((item) => ({
      label: labelName,
      value: valueName,
      ...item
    }));

    // setSelectedOption(value ? value : (result[0]?.value ?? ''));
    // value가 없는 경우 기본값을 설정하지 않음
    setSelectedOption(value ?? undefined);

    return result;
  }, [labelName, options, valueName, value]);

  const handleSelectOpen = useCallback((isOpen: boolean) => {
    setSelectOpen(isOpen);
  }, []);

  const handleChange = useCallback(
    (e: SelectChangeEvent, child: ReactNode) => {
      const selectedVal = e.target.value;
      setSelectedOption(selectedVal as string | number);

      if (onSelected && React.isValidElement(child)) {
        onSelected((child as ReactElement<MenuItemProps>).props);
      }

      if (sendValue && React.isValidElement(child)) {
        sendValue(e, child as ReactElement<MenuItemProps>);
      }

      handleSelectOpen(false);
    },
    [onSelected, sendValue, handleSelectOpen]
  );

  useEffect(() => {
    if (value !== undefined) {
      setSelectedOption(value);
    }
  }, [value]);

  return (
    <StyledBasicSelect
      width={width}
      marginbottom={marginBottom}
      className={className}
      isusepadding={isUsePadding ? 'true' : ''}
    >
      {label && (
        <span className="bs-label">
          {label}
          {required && <span className="required">*</span>}
        </span>
      )}
      <Select
        name={name}
        label={label}
        disabled={disabled}
        open={selectOpen}
        onOpen={() => handleSelectOpen(true)}
        onClose={() => handleSelectOpen(false)}
        value={selectedOption ?? ''}
        onChange={handleChange}
        IconComponent={() =>
          name === 'language' ? (
            <img src={commonImage.laguage} alt="language" />
          ) : (
            <img
              src={arrowImage.arrowTriangle}
              alt="arrow"
              style={{ cursor: disabled ? 'default' : 'pointer' }}
              onClick={() => {
                if (!disabled) {
                  handleSelectOpen(true);
                }
              }}
            />
          )
        }
        MenuProps={menuProps}
        {...props}
      >
        {selectOptions.map(({ label, value }, i) => {
          return (
            <MenuItem key={i} value={value}>
              {label}
            </MenuItem>
          );
        })}
      </Select>
    </StyledBasicSelect>
  );
};

const menuProps = {
  PaperProps: {
    sx: {
      bgcolor: `${themeOptions.palette.white.main}`,
      padding: '5px',
      margin: '8px 0 0 0',
      borderRadius: '6px',
      border: '1px solid #dadada',
      boxShadow: '0px 4px 10px 0px #0000001A',
      boxSizing: 'border-box',
      '& .MuiMenuItem-root': {
        height: '40px',
        '&:hover': {
          borderRadius: '4px'
        }
      },
      '& .MuiList-root': {
        padding: 0
      },
      '& .Mui-selected': {
        bgcolor: `var(--color-secondary-blue) !important`,
        borderRadius: '4px',
        color: `${themeOptions.palette.white.main}`
      }
    }
  }
};
